import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useDemo } from "@/contexts/DemoContext";
import { useDemoStoreOptional } from "@/contexts/DemoStoreContext";
import { useAuth } from "@/contexts/AuthContext";

export function useBusinessSettings() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { isDemo } = useDemo();
  const store = useDemoStoreOptional();
  const { profile: authProfile } = useAuth();

  const businessId = authProfile?.business_id;

  const { data: business, isLoading } = useQuery({
    queryKey: ["business-settings", businessId],
    enabled: !isDemo && !!businessId,
    queryFn: async () => {
      const { data, error } = await supabase.from("businesses").select("*").eq("id", businessId!).maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (updates: Record<string, unknown>) => {
      if (isDemo && store) { store.updateBusiness(updates); return; }
      if (!business?.id) throw new Error("No business found");
      const { error } = await supabase.from("businesses").update(updates).eq("id", business.id);
      if (error) throw error;
    },
    onSuccess: () => {
      if (!isDemo) { queryClient.invalidateQueries({ queryKey: ["business-settings"] }); toast({ title: "Settings saved" }); }
      else { toast({ title: "Settings updated" }); }
    },
    onError: (error: Error) => { toast({ title: "Error saving settings", description: error.message, variant: "destructive" }); },
  });

  return {
    business: isDemo ? (store?.business ?? null) : business,
    isLoading: isDemo ? false : isLoading,
    updateBusiness: updateMutation.mutateAsync,
    isSaving: updateMutation.isPending,
  };
}
